<template>
  <!-- Only render the layout if subscription data is loaded -->
  <SettingsLayout v-if="workspaceStore.subscriptionLoaded">
    <div class="settings-content">
      <div class="heading">
        <h1 class="h2">Plans</h1>
      </div>

      <!-- App Store / Google Play subscriptions can't be changed from the web
           (the Stripe portal doesn't apply). Surface where to manage them
           instead of silently failing on a manage/downgrade click. -->
      <div v-if="storeManageNotice" class="store-manage-note" role="status">
        <p v-if="isGoogleManaged">
          Your subscription was purchased through Google Play and is managed in
          the Play Store. To change or cancel it, open the
          <strong>Play Store</strong> app on your Android device, tap your
          profile icon, then tap
          <strong>Payments &amp; subscriptions &rsaquo; Subscriptions</strong>
          and select AudioScribe.
        </p>
        <p v-if="isAppleManaged">
          Your subscription was purchased through Apple and is managed in the
          App Store. To change or cancel it, open the
          <strong>Settings</strong> app on your iPhone or iPad, tap your name,
          then tap <strong>Subscriptions</strong> and select AudioScribe.
        </p>
      </div>

      <!-- A plan AudioScribe granted: there's nothing to buy or manage. -->
      <div v-if="compPlanNotice" class="store-manage-note" role="status">
        <p>
          Your Pro plan was provided by AudioScribe, so there's nothing to buy
          or manage here.
        </p>
      </div>

      <div class="block">
        <div class="mxw">
          <!-- Show the toggle if there's no subscription or if the user is on a monthly plan -->
          <div class="watermark-toggle" v-if="showYearlyToggle">
            <label class="checkbox-wrap">
              <input
                type="checkbox"
                id="toggle"
                v-model="localIsYearly"
                @change="handleYearlyToggle"
              />
              <span class="label" style="padding-right: 20px"
                >Monthly Billing</span
              >
              <span class="toggle">
                <span class="circle"></span>
              </span>
              <span class="label">Yearly Billing</span>
              <a href="#" class="badge">Save 50%</a>
            </label>
          </div>
        </div>
      </div>

      <div class="plans-holder">
        <!-- Free Plan -->
        <div class="plan-single">
          <div class="head">
            <h2 class="h3">Free</h2>
          </div>
          <div class="price">
            <span class="unit">$</span>
            <span class="amount">0</span>
          </div>
          <!-- If user has a subscription, show "Downgrade Plan". Otherwise show "Current Plan" -->
          <a
            v-if="workspaceStore.subscription"
            @click.prevent="navigateToBillingPortal(true)"
            href="#"
            class="plan-btn"
          >
            Downgrade Plan
          </a>
          <a v-else href="#" class="plan-btn">
            <i class="icon-check"></i>Current Plan
          </a>

          <strong class="title">Key Features</strong>
          <ul class="features-list">
            <li>3 free files per day</li>
            <li>AI Summaries</li>
            <li>Powerful AI chat feature</li>
            <li>Search</li>
            <li>Support for audio and video content</li>
            <li>Export in multiple formats (TXT, SRT, VTT)</li>
          </ul>
          <br />
          <ul class="features-list negative-features">
            <li>Transcripts capped to 25 mins</li>
            <li>Lower processing priority</li>
          </ul>
        </div>

        <!-- Pro Plan (lookup keys stay standard_*_v1) -->
        <div class="plan-single most-popular">
          <div class="head">
            <h2 class="h3">Pro</h2>
            <!-- Show the "Save $120" badge if user toggles yearly -->
            <a v-if="localIsYearly" href="#" class="badge">Save $120</a>
          </div>
          <div class="price">
            <span class="unit">$</span>
            <span class="amount">{{ displayPrice.amount }}</span>
            <span style="padding-left: 5px">Per month</span>
            <span v-if="localIsYearly" class="note">{{
              displayPrice.note
            }}</span>
          </div>

          <!-- Highlight "current-plan" if the active plan matches -->
          <a
            @click.prevent="handleUpgrade"
            class="plan-btn btn-upgrade"
            :class="{
              'current-plan': isCurrentPlan(
                localIsYearly ? 'standard_yearly_v1' : 'standard_monthly_v1',
              ),
              'is-busy': isCheckingOut,
            }"
          >
            {{ upgradeButtonText }}
          </a>

          <strong class="title">Key Features</strong>
          <ul class="features-list">
            <li>Unlimited number of transcripts</li>
            <li>Up to 10-hour transcripts per file</li>
            <li>Highest Processing Priority</li>
            <li>Export in multiple formats (TXT, PDF, SRT)</li>
            <li>Full-index search</li>
            <li>AI Summaries</li>
            <li>Powerful AI chat feature</li>
            <li>Speaker Identification</li>
          </ul>
        </div>

        <!-- Enterprise Plan -->
        <div class="plan-single">
          <div class="head">
            <h2 class="h3">Enterprise</h2>
          </div>
          <div class="price">
            <span class="unit"></span>
            <span class="amount">-</span>
          </div>
          <a
            href="mailto:support@audioscribe.io"
            @click="trackContactSales"
            class="plan-btn"
            >Contact Us</a
          >
          <strong class="title">Key Features</strong>
          <ul class="features-list">
            <li>Everything in the Pro Plan</li>
            <li>Running in your own private cloud environment</li>
          </ul>
        </div>
      </div>
    </div>
  </SettingsLayout>

  <!-- Otherwise, show a loading spinner or text -->
  <div v-else style="text-align: center; margin: 2rem">
    <p>Loading your subscription info...</p>
  </div>
</template>

<script>
import SettingsLayout from "@/components/SettingsLayout.vue";
import {
  createCheckoutSession,
  createCustomerPortalSession,
} from "@/services/workspace.service.js";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";

export default {
  name: "SettingsPlansView",
  components: {
    SettingsLayout,
  },
  data() {
    return {
      // Tracks the user's desired yearly toggle state.
      // This can differ from the store if the user is subscribed monthly but wants to see yearly pricing.
      localIsYearly: false,
      // True from Upgrade click until the Stripe redirect fires — blocks repeat
      // clicks that would create duplicate checkout sessions.
      isCheckingOut: false,
      // Shown when an App Store / Play Store subscriber clicks a
      // manage/downgrade action — those route through the Stripe portal,
      // which doesn't apply to them.
      storeManageNotice: false,
      // Shown when someone on a plan AudioScribe granted clicks a
      // manage/upgrade action: there's no Stripe customer to send them to.
      compPlanNotice: false,
    };
  },
  computed: {
    ...mapStores(useWorkSpaceStore),

    // Which providers grant the plan. The entitlement's `sources`
    // ({ stripe, apple, google }, each non-null only when that provider
    // grants access) decides when the API sends it; it resolves "both".
    // Older responses fall back to `subscriptionSource`, where "both" means
    // a Stripe subscription is among them.
    planProviders() {
      const sources = this.workspaceStore.subscriptionSources;
      if (sources) {
        return {
          stripe: Boolean(sources.stripe),
          apple: Boolean(sources.apple),
          google: Boolean(sources.google),
        };
      }
      const source = this.workspaceStore.subscriptionSource;
      return {
        stripe: source === "stripe" || source === "both",
        apple: source === "apple",
        google: source === "google_play",
      };
    },

    // A plan AudioScribe granted: no Stripe customer, nothing to buy or
    // manage.
    isCompPlan() {
      return this.workspaceStore.subscriptionSource === "comp";
    },

    // Only the App Store and/or Google Play grant the plan. Those are
    // managed in the stores: never send these users to Stripe checkout or
    // the Stripe portal.
    isStoreManaged() {
      const { stripe, apple, google } = this.planProviders;
      return !stripe && (apple || google);
    },

    // Bought through Apple (App Store): managed on-device.
    isAppleManaged() {
      return this.isStoreManaged && this.planProviders.apple;
    },

    // Bought through Google Play: managed in the Play Store.
    isGoogleManaged() {
      return this.isStoreManaged && this.planProviders.google;
    },

    // Already on a plan Stripe may bill. Upgrade must open the billing
    // portal, never a new checkout: a Stripe plan that isn't exactly ACTIVE
    // (e.g. TRIALING or PAST_DUE) would otherwise get a second subscription.
    hasPlan() {
      return (
        this.workspaceStore.isSubscribedState === true ||
        this.workspaceStore.subscription?.status === "ACTIVE" ||
        this.planProviders.stripe
      );
    },

    // Return different price info based on localIsYearly
    displayPrice() {
      if (this.localIsYearly) {
        return {
          amount: "10.00",
          note: "Billed Annually as $120",
        };
      }
      return { amount: "19.99", note: "" };
    },

    // Only show the toggle if no subscription or if the subscription is monthly
    showYearlyToggle() {
      const sub = this.workspaceStore.subscription;
      if (!sub) return true;
      return sub.billingCycle === "MONTH";
    },

    // Determines the text for the "Upgrade" button
    upgradeButtonText() {
      if (this.isCheckingOut) {
        return "Redirecting to checkout…";
      }
      if (this.isCompPlan) {
        return "Current Plan";
      }
      // A store subscriber can't upgrade/manage via Stripe here.
      if (this.isAppleManaged && this.isGoogleManaged) {
        return "Manage Subscription";
      }
      if (this.isAppleManaged) {
        return "Manage in App Store";
      }
      if (this.isGoogleManaged) {
        return "Manage in Google Play";
      }
      const sub = this.workspaceStore.subscription;
      if (sub && sub.status === "ACTIVE") {
        // If user is already on the toggled plan, show "Current Plan"
        if (
          this.isCurrentPlan(
            this.localIsYearly ? "standard_yearly_v1" : "standard_monthly_v1",
          )
        ) {
          return "Current Plan";
        }
        // If toggling from monthly to yearly
        else if (
          this.localIsYearly &&
          sub.currentPlanName === "standard_monthly_v1"
        ) {
          return "Upgrade Plan";
        } else {
          return "Upgrade";
        }
      }
      // On a plan that isn't ACTIVE (e.g. trialing or past due): the button
      // opens the billing portal to manage it.
      if (this.hasPlan) {
        return "Manage Plan";
      }
      // If user does not have a subscription, always show "Upgrade Plan"
      return "Upgrade Plan";
    },
  },
  methods: {
    // Called when user clicks the "Upgrade" button
    async handleUpgrade() {
      if (this.isCheckingOut) {
        return;
      }
      // If we couldn't confirm the current plan, don't guess: sending a paying
      // user to checkout could start a second subscription.
      if (!this.workspaceStore.subscriptionStatusSucceeded) {
        this.$toast.error(
          "We couldn't confirm your current plan. Please refresh the page and try again.",
        );
        return;
      }
      const sub = this.workspaceStore.subscription;
      const billingCycle = this.localIsYearly ? "yearly" : "monthly";
      const price = this.localIsYearly ? 120 : 19.99;

      // Track upgrade clicked
      analytics.plansUpgradeClicked({
        plan: "standard",
        billing_cycle: billingCycle,
        price: price,
        current_tier: sub?.currentPlanName || "free",
      });

      // A store (App Store / Play) subscriber must NEVER be sent to Stripe —
      // neither the portal nor a new checkout. This has to gate BEFORE the
      // status branch: a store sub that isn't exactly "ACTIVE" (e.g.
      // TRIALING) would otherwise fall through to createCheckoutSession and
      // double-charge them on Stripe. Point them to their store instead.
      if (this.isStoreManaged) {
        this.storeManageNotice = true;
        return;
      }

      // Anyone already on a plan goes to the billing portal, never to a new
      // checkout: a Stripe plan that isn't exactly ACTIVE (e.g. TRIALING or
      // PAST_DUE), or the Stripe part of "both", would otherwise get a
      // SECOND Stripe subscription. A plan AudioScribe granted also takes
      // this path; navigateToBillingPortal shows it the "nothing to manage"
      // notice instead of the portal.
      if (this.hasPlan) {
        this.navigateToBillingPortal();
      } else {
        // Otherwise, create a new checkout session
        this.isCheckingOut = true;
        try {
          const billingPeriod = this.localIsYearly ? "yearly" : "monthly";

          // Track checkout initiated before redirect
          analytics.checkoutInitiated({
            plan: "standard",
            billing_cycle: billingPeriod,
            price: price,
          });

          // The API helper resolves HTTP failures as { error }.
          const { data, error } =
            (await createCheckoutSession(billingPeriod)) || {};
          if (error || !data?.stripeCheckoutSessionUrl) {
            throw new Error(error?.message || "No checkout URL");
          }
          // Stay disabled until the browser navigates away — re-enabling here
          // would reopen the duplicate-session window during the redirect.
          globalThis.location.href = data.stripeCheckoutSessionUrl;
          return;
        } catch (error) {
          console.error("Error during checkout session creation", error);
          this.$toast.error("Couldn't start checkout. Please try again.");
          this.isCheckingOut = false;
        }
      }
    },

    // Fetch subscription status from your workspace store
    async fetchSubscriptionStatus() {
      try {
        await this.workspaceStore.fetchSubscriptionStatus();
        // If the user is on a yearly plan, set localIsYearly = true
        const sub = this.workspaceStore.subscription;
        if (sub && sub.billingCycle === "YEAR") {
          this.localIsYearly = true;
        } else {
          this.localIsYearly = false;
        }
      } catch (error) {
        console.error("Error fetching subscription status", error);
      }
    },

    // Opens the Stripe billing portal
    async navigateToBillingPortal(isDowngrade = false) {
      // Track billing portal opened
      const sub = this.workspaceStore.subscription;
      if (isDowngrade) {
        analytics.plansDowngradeClicked({
          current_tier: sub?.currentPlanName || "standard",
        });
      }

      // A plan AudioScribe granted has no Stripe customer to manage.
      if (this.isCompPlan) {
        this.compPlanNotice = true;
        return;
      }

      // App Store / Play subscriptions aren't managed through the Stripe
      // portal (the endpoint 400s for them). Point the user to their store
      // instead of silently doing nothing.
      if (this.isStoreManaged) {
        this.storeManageNotice = true;
        return;
      }

      analytics.billingPortalOpened();

      try {
        // The API helper resolves HTTP failures as { error }.
        const { data, error } = (await createCustomerPortalSession()) || {};
        if (error || !data?.customerPortalUrl) {
          throw new Error(error?.message || "No portal URL");
        }
        globalThis.location.href = data.customerPortalUrl;
      } catch (error) {
        console.error("Failed to navigate to billing portal", error);
        this.$toast.error(
          "Couldn't open billing management. Please try again.",
        );
      }
    },

    // Called when toggling the checkbox for yearly/monthly
    handleYearlyToggle() {
      // Track billing cycle toggle
      analytics.plansBillingCycleToggled({
        selected_cycle: this.localIsYearly ? "yearly" : "monthly",
      });

      // If toggling from monthly to yearly while on a monthly plan,
      // force an update so the button text says "Upgrade Plan"
      const sub = this.workspaceStore.subscription;
      if (
        this.localIsYearly &&
        sub &&
        sub.currentPlanName === "standard_monthly_v1"
      ) {
        this.$forceUpdate();
      }
    },

    // Checks if the user is on a specific plan
    isCurrentPlan(planName) {
      const sub = this.workspaceStore.subscription;
      return sub && sub.currentPlanName === planName;
    },

    // Track contact sales click
    trackContactSales() {
      analytics.plansContactSalesClicked();
    },

    // Browser Back from Stripe can restore this page from bfcache with the
    // in-flight flag still set — reset it so the button isn't stuck.
    handlePageShow(event) {
      if (event.persisted) {
        this.isCheckingOut = false;
      }
    },
  },
  mounted() {
    // Track page view
    const sub = this.workspaceStore.subscription;
    analytics.settingsPlansViewed({
      current_tier: sub?.currentPlanName || "free",
      billing_cycle: sub?.billingCycle || null,
    });

    this.fetchSubscriptionStatus();

    globalThis.addEventListener("pageshow", this.handlePageShow);
  },
  beforeUnmount() {
    globalThis.removeEventListener("pageshow", this.handlePageShow);
  },
};
</script>

<style scoped>
.block {
  border-bottom: none;
  display: flex;
  justify-content: center;
}

.note {
  font-size: 12px;
  color: #888;
  display: block;
  margin-top: 5px;
}

.store-manage-note {
  max-width: 640px;
  margin: 0 auto 1rem;
  padding: 12px 16px;
  border: 1px solid #d9d9e3;
  border-radius: 8px;
  background: #f6f6fb;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

/* Highlight the button if it's the current plan */
.current-plan {
  background-color: #ccc;
  pointer-events: none;
}

/* Checkout session creation in flight — block further clicks */
.is-busy {
  opacity: 0.6;
  pointer-events: none;
}
</style>
